// Every user-visible string in the widget goes through these label sets. Placeholders use
// `{name}`. German is Sie-form throughout; error and field codes mirror the API contract.

export const en = {
  loading: 'Loading…',
  retry: 'Try again',
  retryIn: 'Try again ({seconds}s)',
  back: 'Back',
  optional: 'optional',
  required: 'required',
  poweredBy: 'Powered by {name}',
  languageLabel: 'Language',

  // Event details
  eventDuration: '{minutes} min',
  eventWith: 'with {names}',
  locationGoogleMeet: 'Google Meet (link in your confirmation)',
  locationPhone: 'Phone call',
  locationInPerson: 'In person',
  locationCustom: 'Location',

  // Calendar
  calendarPrevMonth: 'Previous month',
  calendarNextMonth: 'Next month',
  calendarSelectDate: 'Select a date',
  calendarNoDates: 'No available dates this month.',
  calendarDayUnavailable: 'no times available',
  calendarToday: 'today',

  // Slots
  slotsPickDate: 'Pick a date to see available times.',
  slotsNone: 'No times available on this day.',
  slotsShownIn: 'Times shown in {zone}',
  slotsHolding: 'Reserving…',
  slotsDegraded: 'Some calendars could not be checked; availability may be incomplete.',

  // Time zone combobox
  timeZoneLabel: 'Time zone',
  timeZoneSearch: 'Search time zones',
  timeZoneNoResults: 'No matching time zones.',

  // Hold countdown
  holdRemaining: 'Time slot held for {time}',
  holdAnnounceStart: 'Your time slot is reserved for {minutes} minutes.',
  holdAnnounceMinutes: '{minutes} minutes left to complete your booking.',
  holdAnnounceOneMinute: '1 minute left to complete your booking.',
  holdAnnounceSeconds: '{seconds} seconds left to complete your booking.',
  holdExpired: 'Your reservation has expired. Please choose a time again.',
  holdChooseAgain: 'Choose another time',

  // Form
  formTitle: 'Your details',
  formName: 'Name',
  formEmail: 'Email',
  formPhone: 'Phone',
  formPhoneCountry: 'Country',
  formPhoneCountryPlaceholder: 'Select country',
  formMoreCountries: 'More countries…',
  formSelectPlaceholder: 'Please select',
  formChangeTime: 'Change time',
  formSubmit: 'Confirm booking',
  formSubmitting: 'Booking…',
  formErrorSummary: 'Please check the highlighted fields.',

  // Field codes (API contract) + client-side extras
  fieldRequired: 'This field is required.',
  fieldInvalidEmailFormat: 'Please enter a valid email address.',
  fieldInvalidPhone: 'Please enter a valid phone number.',
  fieldPhoneCountryRequired: 'Please select the country of your phone number.',
  fieldInvalidTimezone: 'Please choose a valid time zone.',
  fieldInvalidDate: 'Please choose a valid date.',
  fieldTooLong: 'This entry is too long.',
  fieldInvalidChoice: 'Please choose one of the offered options.',
  fieldInvalidUrl: 'Please enter a valid web address (https://…).',
  fieldInvalidSlug: 'This value is not valid.',
  fieldMinChoices: 'Please select at least {min}.',
  fieldMaxChoices: 'Please select at most {max}.',
  fieldInvalid: 'Please check this entry.',

  // Error codes (API contract)
  errorValidationFailed: 'Please check the highlighted fields.',
  errorUnauthorized: 'This booking page is not authorised for this website.',
  errorForbidden: 'This action is not permitted.',
  errorNotFound: 'This booking page could not be found.',
  errorRateLimited: 'Too many requests. Please wait {seconds} seconds and try again.',
  errorSlotUnavailable: 'This time was just taken. Please choose another one.',
  errorSlotUnavailableNext: 'This time was just taken. The next available time is {time}.',
  errorSlotUnavailableShow: 'Show it',
  errorHoldExpired: 'Your reservation has expired. Please choose a time again.',
  errorConfirmInProgress: 'Your booking is already being confirmed. Please wait a moment.',
  errorProviderUnavailable:
    'The calendar is temporarily unavailable. Your time is still reserved — please try again in a moment.',
  errorBookingNotActive: 'This booking is no longer active.',
  errorBookingSuperseded: 'This booking was rescheduled. The current booking is {ref}.',
  errorBookingSupersededLink: 'Open current booking',
  errorPolicyViolation: 'This action is not allowed for this booking.',
  errorInvalidToken: 'This link is invalid or has expired.',
  errorInvalidTransition: 'This booking cannot be changed in its current state.',
  errorPlanLimit: 'This account has reached a plan limit.',
  errorAuthModeDisabled: 'This sign-in method is disabled.',
  errorEmailNotVerified: 'The email address has not been verified yet.',
  errorInvalidCredentials: 'The sign-in details are incorrect.',
  errorConflict: 'This value is already in use.',
  errorFeatureDisabled: 'This feature is not available.',
  errorInternalError: 'Something went wrong on our side. Please try again.',
  errorNetworkError: 'The connection failed. Please check your internet connection and try again.',
  errorHttpError: 'The request failed. Please try again.',
  errorUnknown: 'Something went wrong. Please try again.',

  // Policy codes
  policyRescheduleNotAllowed: 'This booking cannot be rescheduled online. Please contact us.',
  policyCancelNotAllowed: 'This booking cannot be cancelled online. Please contact us.',
  policyRescheduleMinNotice: 'Rescheduling is only possible up to {notice} before the start.',
  policyCancelMinNotice: 'Cancelling is only possible up to {notice} before the start.',
  policyAlreadyStarted: 'This appointment has already started.',

  // Confirmation
  confirmationTitle: 'You are booked!',
  confirmationBody: '{eventType} on {date}, {range} ({zone}) is confirmed.',
  confirmationInvite: 'A calendar invitation with all details is on its way to {email}.',
  confirmationAddToCalendar: 'Accept the invitation to add it to your calendar.',
  confirmationManage: 'Reschedule or cancel',
  confirmationReference: 'Reference {ref}',
  confirmationJoin: 'Join the video call',

  // Manage
  manageTitle: 'Your booking',
  manageReschedule: 'Reschedule',
  manageCancel: 'Cancel booking',
  manageCancelTitle: 'Cancel this booking?',
  manageCancelReason: 'Reason',
  manageCancelConfirm: 'Yes, cancel booking',
  manageKeep: 'Keep booking',
  manageCancelled: 'Your booking has been cancelled.',
  manageRescheduleTitle: 'Choose a new time',
  manageRescheduleReason: 'Reason for rescheduling',
  manageRescheduleConfirm: 'Confirm new time',
  manageRescheduled: 'Your booking has been moved.',
  manageNewTime: 'New time',
  managePreviously: 'Previously {range}',
  manageOpenCurrent: 'Open current booking',
  manageBookedFor: 'Booked for {email}',

  // Booking statuses
  statusPendingHold: 'Pending',
  statusExpired: 'Expired',
  statusConfirmed: 'Confirmed',
  statusRescheduled: 'Rescheduled',
  statusCancelled: 'Cancelled',
  statusCompleted: 'Completed',
  statusNoShow: 'No-show',
};

export type Labels = typeof en;
export type LabelKey = keyof Labels;

export const de: Labels = {
  loading: 'Wird geladen…',
  retry: 'Erneut versuchen',
  retryIn: 'Erneut versuchen ({seconds} s)',
  back: 'Zurück',
  optional: 'optional',
  required: 'Pflichtfeld',
  poweredBy: 'Bereitgestellt von {name}',
  languageLabel: 'Sprache',

  eventDuration: '{minutes} Min.',
  eventWith: 'mit {names}',
  locationGoogleMeet: 'Google Meet (Link in Ihrer Bestätigung)',
  locationPhone: 'Telefonat',
  locationInPerson: 'Vor Ort',
  locationCustom: 'Ort',

  calendarPrevMonth: 'Vorheriger Monat',
  calendarNextMonth: 'Nächster Monat',
  calendarSelectDate: 'Datum wählen',
  calendarNoDates: 'In diesem Monat sind keine Termine verfügbar.',
  calendarDayUnavailable: 'keine Termine verfügbar',
  calendarToday: 'heute',

  slotsPickDate: 'Wählen Sie ein Datum, um verfügbare Zeiten zu sehen.',
  slotsNone: 'An diesem Tag sind keine Zeiten verfügbar.',
  slotsShownIn: 'Zeiten in {zone}',
  slotsHolding: 'Wird reserviert…',
  slotsDegraded:
    'Einige Kalender konnten nicht geprüft werden; die Verfügbarkeit ist möglicherweise unvollständig.',

  timeZoneLabel: 'Zeitzone',
  timeZoneSearch: 'Zeitzone suchen',
  timeZoneNoResults: 'Keine passende Zeitzone gefunden.',

  holdRemaining: 'Termin reserviert für {time}',
  holdAnnounceStart: 'Ihr Termin ist für {minutes} Minuten reserviert.',
  holdAnnounceMinutes: 'Noch {minutes} Minuten, um die Buchung abzuschliessen.',
  holdAnnounceOneMinute: 'Noch 1 Minute, um die Buchung abzuschliessen.',
  holdAnnounceSeconds: 'Noch {seconds} Sekunden, um die Buchung abzuschliessen.',
  holdExpired: 'Ihre Reservierung ist abgelaufen. Bitte wählen Sie erneut eine Zeit.',
  holdChooseAgain: 'Andere Zeit wählen',

  formTitle: 'Ihre Angaben',
  formName: 'Name',
  formEmail: 'E-Mail',
  formPhone: 'Telefon',
  formPhoneCountry: 'Land',
  formPhoneCountryPlaceholder: 'Land wählen',
  formMoreCountries: 'Weitere Länder…',
  formSelectPlaceholder: 'Bitte wählen',
  formChangeTime: 'Zeit ändern',
  formSubmit: 'Termin buchen',
  formSubmitting: 'Wird gebucht…',
  formErrorSummary: 'Bitte überprüfen Sie die markierten Felder.',

  fieldRequired: 'Dieses Feld ist erforderlich.',
  fieldInvalidEmailFormat: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
  fieldInvalidPhone: 'Bitte geben Sie eine gültige Telefonnummer ein.',
  fieldPhoneCountryRequired: 'Bitte wählen Sie das Land Ihrer Telefonnummer.',
  fieldInvalidTimezone: 'Bitte wählen Sie eine gültige Zeitzone.',
  fieldInvalidDate: 'Bitte wählen Sie ein gültiges Datum.',
  fieldTooLong: 'Diese Eingabe ist zu lang.',
  fieldInvalidChoice: 'Bitte wählen Sie eine der angebotenen Optionen.',
  fieldInvalidUrl: 'Bitte geben Sie eine gültige Webadresse ein (https://…).',
  fieldInvalidSlug: 'Dieser Wert ist ungültig.',
  fieldMinChoices: 'Bitte wählen Sie mindestens {min}.',
  fieldMaxChoices: 'Bitte wählen Sie höchstens {max}.',
  fieldInvalid: 'Bitte überprüfen Sie diese Eingabe.',

  errorValidationFailed: 'Bitte überprüfen Sie die markierten Felder.',
  errorUnauthorized: 'Diese Buchungsseite ist für diese Website nicht freigeschaltet.',
  errorForbidden: 'Diese Aktion ist nicht erlaubt.',
  errorNotFound: 'Diese Buchungsseite wurde nicht gefunden.',
  errorRateLimited:
    'Zu viele Anfragen. Bitte warten Sie {seconds} Sekunden und versuchen Sie es erneut.',
  errorSlotUnavailable: 'Dieser Termin wurde soeben vergeben. Bitte wählen Sie einen anderen.',
  errorSlotUnavailableNext:
    'Dieser Termin wurde soeben vergeben. Der nächste freie Termin ist {time}.',
  errorSlotUnavailableShow: 'Anzeigen',
  errorHoldExpired: 'Ihre Reservierung ist abgelaufen. Bitte wählen Sie erneut eine Zeit.',
  errorConfirmInProgress: 'Ihre Buchung wird bereits bestätigt. Bitte warten Sie einen Moment.',
  errorProviderUnavailable:
    'Der Kalender ist vorübergehend nicht erreichbar. Ihr Termin bleibt reserviert – bitte versuchen Sie es gleich noch einmal.',
  errorBookingNotActive: 'Diese Buchung ist nicht mehr aktiv.',
  errorBookingSuperseded: 'Diese Buchung wurde verschoben. Die aktuelle Buchung ist {ref}.',
  errorBookingSupersededLink: 'Aktuelle Buchung öffnen',
  errorPolicyViolation: 'Diese Aktion ist für diese Buchung nicht erlaubt.',
  errorInvalidToken: 'Dieser Link ist ungültig oder abgelaufen.',
  errorInvalidTransition: 'Diese Buchung kann in ihrem aktuellen Status nicht geändert werden.',
  errorPlanLimit: 'Für dieses Konto wurde ein Limit des Tarifs erreicht.',
  errorAuthModeDisabled: 'Diese Anmeldemethode ist deaktiviert.',
  errorEmailNotVerified: 'Die E-Mail-Adresse wurde noch nicht bestätigt.',
  errorInvalidCredentials: 'Die Anmeldedaten sind nicht korrekt.',
  errorConflict: 'Dieser Wert wird bereits verwendet.',
  errorFeatureDisabled: 'Diese Funktion ist nicht verfügbar.',
  errorInternalError: 'Bei uns ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
  errorNetworkError:
    'Die Verbindung ist fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
  errorHttpError: 'Die Anfrage ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
  errorUnknown: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',

  policyRescheduleNotAllowed:
    'Diese Buchung kann nicht online verschoben werden. Bitte kontaktieren Sie uns.',
  policyCancelNotAllowed:
    'Diese Buchung kann nicht online storniert werden. Bitte kontaktieren Sie uns.',
  policyRescheduleMinNotice: 'Verschieben ist nur bis {notice} vor Beginn möglich.',
  policyCancelMinNotice: 'Stornieren ist nur bis {notice} vor Beginn möglich.',
  policyAlreadyStarted: 'Dieser Termin hat bereits begonnen.',

  confirmationTitle: 'Ihr Termin ist gebucht!',
  confirmationBody: '{eventType} am {date}, {range} ({zone}) ist bestätigt.',
  confirmationInvite: 'Eine Kalendereinladung mit allen Details ist unterwegs an {email}.',
  confirmationAddToCalendar:
    'Nehmen Sie die Einladung an, um den Termin in Ihren Kalender zu übernehmen.',
  confirmationManage: 'Verschieben oder stornieren',
  confirmationReference: 'Referenz {ref}',
  confirmationJoin: 'Videoanruf beitreten',

  manageTitle: 'Ihre Buchung',
  manageReschedule: 'Verschieben',
  manageCancel: 'Buchung stornieren',
  manageCancelTitle: 'Diese Buchung stornieren?',
  manageCancelReason: 'Grund',
  manageCancelConfirm: 'Ja, Buchung stornieren',
  manageKeep: 'Buchung behalten',
  manageCancelled: 'Ihre Buchung wurde storniert.',
  manageRescheduleTitle: 'Neue Zeit wählen',
  manageRescheduleReason: 'Grund für die Verschiebung',
  manageRescheduleConfirm: 'Neue Zeit bestätigen',
  manageRescheduled: 'Ihre Buchung wurde verschoben.',
  manageNewTime: 'Neue Zeit',
  managePreviously: 'Zuvor {range}',
  manageOpenCurrent: 'Aktuelle Buchung öffnen',
  manageBookedFor: 'Gebucht für {email}',

  statusPendingHold: 'Ausstehend',
  statusExpired: 'Abgelaufen',
  statusConfirmed: 'Bestätigt',
  statusRescheduled: 'Verschoben',
  statusCancelled: 'Storniert',
  statusCompleted: 'Abgeschlossen',
  statusNoShow: 'Nicht erschienen',
};

export const builtInLabels: Record<'en' | 'de', Labels> = { en, de };

export function baseLanguage(locale: string | undefined): 'en' | 'de' {
  return /^de\b/i.test(locale ?? '') ? 'de' : 'en';
}

/** Built-in set for the locale's language, with the host's overrides layered on top. */
export function resolveLabels(locale: string | undefined, overrides?: Partial<Labels>): Labels {
  const base = builtInLabels[baseLanguage(locale)];
  if (!overrides) return base;
  const merged: Labels = { ...base };
  for (const key of Object.keys(overrides) as LabelKey[]) {
    const value = overrides[key];
    if (typeof value === 'string') merged[key] = value;
  }
  return merged;
}

export type LabelVars = Record<string, string | number>;

export function formatLabel(template: string, vars?: LabelVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match
  );
}

const codeToKey = (prefix: string, code: string): string =>
  prefix + code.replace(/(?:^|_)([a-z0-9])/g, (_m, c: string) => c.toUpperCase());

export function labelForErrorCode(labels: Labels, code: string, vars?: LabelVars): string {
  const key = codeToKey('error', code) as LabelKey;
  return formatLabel(labels[key] ?? labels.errorUnknown, vars);
}

export function labelForFieldCode(labels: Labels, code: string, vars?: LabelVars): string {
  const key = codeToKey('field', code) as LabelKey;
  return formatLabel(labels[key] ?? labels.fieldInvalid, vars);
}

export function labelForPolicy(labels: Labels, policy: string, vars?: LabelVars): string {
  const key = codeToKey('policy', policy) as LabelKey;
  return formatLabel(labels[key] ?? labels.errorPolicyViolation, vars);
}

export function labelForStatus(labels: Labels, status: string): string {
  const key = codeToKey('status', status) as LabelKey;
  return labels[key] ?? status;
}
